import { useGetList, type RaRecord } from "ra-core";
import { useMemo, useState } from "react";

import { getFinanceAlerts } from "../../providers/commons/financeAlerts";
import { computeFinanceMetrics } from "../../providers/commons/financeMetrics";
import { addToMonth, toMonth } from "../../providers/commons/financeSchedule";
import type {
  FinanceCharge,
  FinanceContract,
  FinanceExpense,
} from "../../types";

/** Months of history in the overview, the current one included */
export const FINANCE_HISTORY_MONTHS = 12;

/**
 * Upper bound of one read (PostgREST caps responses at 1000 rows). A small
 * software house stays far below it over 12 months; past it the overview says
 * its numbers are partial instead of silently truncating them.
 */
const MAX_RECORDS = 1000;
const SORT_BY_ID = { field: "id", order: "ASC" } as const;

const useRecords = <T extends RaRecord>(
  resource: string,
  filter: Record<string, unknown>,
) => {
  const { data, total, isPending, error } = useGetList<T>(resource, {
    pagination: { page: 1, perPage: MAX_RECORDS },
    sort: SORT_BY_ID,
    filter,
  });
  return {
    data: data ?? [],
    isTruncated: (total ?? 0) > (data?.length ?? 0),
    isPending,
    error,
  };
};

const UNPAID_CHARGE = { "paid_at@is": null, "cancelled_at@is": null };

/**
 * Loads what the overview needs: every contract, the charges and expenses of
 * the window (history + future), and the older ones still unpaid (they are
 * overdue). The numbers come from the same pure functions as the "generate the
 * month" action, so the dashboard and the generated rows always agree.
 */
export const useFinanceData = () => {
  const [today] = useState(() => new Date());
  const windowStart = addToMonth(toMonth(today), 1 - FINANCE_HISTORY_MONTHS);
  const contracts = useRecords<FinanceContract>("finance_contracts", {});
  const charges = useRecords<FinanceCharge>("finance_charges", {
    "due_date@gte": windowStart,
  });
  const oldOpenCharges = useRecords<FinanceCharge>("finance_charges", {
    ...UNPAID_CHARGE,
    "due_date@lt": windowStart,
  });
  const expenses = useRecords<FinanceExpense>("finance_expenses", {
    "due_date@gte": windowStart,
  });
  const oldOpenExpenses = useRecords<FinanceExpense>("finance_expenses", {
    "paid_at@is": null,
    "due_date@lt": windowStart,
  });
  const queries = [
    contracts,
    charges,
    oldOpenCharges,
    expenses,
    oldOpenExpenses,
  ];
  const isPending = queries.some((query) => query.isPending);

  const result = useMemo(() => {
    if (isPending) return null;
    const input = {
      contracts: contracts.data,
      charges: [...charges.data, ...oldOpenCharges.data],
      expenses: [...expenses.data, ...oldOpenExpenses.data],
      today,
    };
    return {
      metrics: computeFinanceMetrics({
        ...input,
        months: FINANCE_HISTORY_MONTHS,
      }),
      alerts: getFinanceAlerts(input),
      isEmpty: input.contracts.length === 0 && input.charges.length === 0,
    };
  }, [
    isPending,
    contracts.data,
    charges.data,
    oldOpenCharges.data,
    expenses.data,
    oldOpenExpenses.data,
    today,
  ]);

  return {
    ...result,
    isPending,
    error: queries.find((query) => query.error)?.error ?? null,
    isTruncated: queries.some((query) => query.isTruncated),
  };
};
